import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { PageDetailLayout } from '@/shared/layout';
import { useMaintenanceRouteDetail, useRouteStops, useMaintenanceRoutes } from './hooks/useRoutes';
import { Button, Badge, Card, FeaturePlaceholder, Alert, KPICard, Table, Avatar } from '@/components/ui';
import { RouteFormModal } from './components/RouteFormModal';
import { AuthButton } from '@/core/permissions/AuthButton';
import { useAuth } from '@/core/auth/AuthContext';
import { toast } from 'sonner';

const statusColors: Record<string, string> = {
  draft: 'bg-slate-100 text-slate-500',
  active: 'bg-emerald-100 text-emerald-600',
  inactive: 'bg-slate-100 text-slate-400',
  assigned: 'bg-blue-100 text-blue-600',
  in_progress: 'bg-amber-100 text-amber-600',
  completed: 'bg-indigo-100 text-indigo-600',
  cancelled: 'bg-red-100 text-red-600',
};

const statusLabels: Record<string, string> = {
  draft: 'Bản nháp',
  active: 'Hoạt động',
  assigned: 'Đã giao tuyến',
  in_progress: 'Đang thực hiện',
  completed: 'Hoàn thành',
  cancelled: 'Đã hủy',
};

export default function RouteDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { profile } = useAuth();
  const companyId = profile?.company_id || '';

  const { data: item, isLoading } = useMaintenanceRouteDetail(id);
  const { data: stops = [], isLoading: isLoadingStops } = useRouteStops(id);
  const { updateRoute } = useMaintenanceRoutes(companyId);

  const [activeTab, setActiveTab] = useState('overview');
  const [showEdit, setShowEdit] = useState(false);

  if (isLoading) return <div className="p-12 text-center text-slate-500 text-sm">Đang tải chi tiết tuyến...</div>;
  if (!item) return <div className="p-12 text-center text-red-500 font-bold">Không tìm thấy dữ liệu tuyến</div>;

  const tabs = [
    { value: 'overview', label: 'Tổng quan' },
    { value: 'stops', label: 'Điểm dừng (Stops)' },
    { value: 'tasks', label: 'Công việc' },
    { value: 'history', label: 'Nhật ký tuyến' },
    { value: 'map', label: 'Bản đồ tuyến' },
  ];

  return (
    <PageDetailLayout
      title={item.name}
      description={
        <div className="flex items-center gap-3 mt-1">
          <Badge className={`${statusColors[item.status_code] || 'bg-slate-100 text-slate-600'} border-transparent text-[10px] font-bold `}>
            {statusLabels[item.status_code] || item.status_code}
          </Badge>
          <span className="text-sm text-slate-400 font-mono">{item.code}</span>
          <span className="text-slate-300">|</span>
          <span className="text-sm text-slate-500 flex items-center gap-1">
            <i className="ri-team-line" />
            {item.team?.name || 'Chưa gán tổ'}
          </span>
          <span className="text-slate-300">|</span>
          <span className="text-sm text-slate-500 flex items-center gap-1">
            <i className="ri-map-pin-2-line" />
            {item.project?.name}
          </span>
        </div>
      }
      breadcrumbs={[
        { label: 'Tuyến bảo dưỡng', path: '/maintenance/routes' },
        { label: item.name }
      ]}
      headerActions={
        <div className="flex gap-2">
          <Button variant="outline" icon="ri-calendar-check-line">Giao lịch trình</Button>
          <AuthButton module="maintenance" action="update" variant="outline" icon="ri-edit-line" onClick={() => setShowEdit(true)}>
            Chỉnh sửa
          </AuthButton>
        </div>
      }
      tabs={tabs}
      activeTab={activeTab}
      onTabChange={setActiveTab}
    >
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {activeTab === 'overview' && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <KPICard title="Tổng điểm dừng" value={stops.length} icon="ri-map-pin-line" variant="info" />
                <KPICard title="Tổng công việc" value={stops.reduce((acc, s) => acc + (s.tasks?.length || 0), 0)} icon="ri-list-check-line" variant="success" />
                <KPICard title="Thời gian ước tính" value={stops.reduce((acc, s) => acc + (s.estimated_duration_minutes || 0), 0)} icon="ri-time-line" variant="neutral" suffix="ph" />
              </div>

              <Card className="p-6">
                <h3 className="font-bold text-slate-900 mb-6 flex items-center gap-2">
                  <i className="ri-information-line text-os-primary" />
                  Mô tả tuyến đường
                </h3>
                <p className="text-sm text-slate-600 leading-relaxed bg-slate-50 p-4 rounded-2xl border border-slate-100 italic">
                  {item.description || 'Chưa có mô tả chi tiết cho tuyến bảo dưỡng này.'}
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-y-4 gap-x-12 mt-6">
                  <div className="flex justify-between border-b border-slate-50 pb-2">
                    <span className="text-sm text-slate-500">Người phụ trách</span>
                    <div className="flex items-center gap-2">
                      <Avatar src={item.assignee?.avatar_url} name={item.assignee?.full_name} size="xs" />
                      <span className="text-sm font-bold text-slate-700">{item.assignee?.full_name || '---'}</span>
                    </div>
                  </div>
                  <div className="flex justify-between border-b border-slate-50 pb-2">
                    <span className="text-sm text-slate-500">Mã định danh</span>
                    <span className="text-sm font-bold text-os-primary">{item.code}</span>
                  </div>
                  <div className="flex justify-between border-b border-slate-50 pb-2">
                    <span className="text-sm text-slate-500">Ngày tạo</span>
                    <span className="text-sm font-bold text-slate-700">{new Date(item.created_at).toLocaleDateString('vi-VN')}</span>
                  </div>
                  <div className="flex justify-between border-b border-slate-50 pb-2">
                    <span className="text-sm text-slate-500">Cập nhật cuối</span>
                    <span className="text-sm font-bold text-slate-700">{new Date(item.updated_at).toLocaleDateString('vi-VN')}</span>
                  </div>
                </div>
              </Card>

              <Card className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="font-bold text-slate-900 flex items-center gap-2">
                    <i className="ri-list-ordered text-os-primary" />
                    Danh sách điểm dừng theo thứ tự
                  </h3>
                  <Button variant="ghost" size="sm" className="text-os-primary text-xs font-bold" onClick={() => setActiveTab('stops')}>
                    Quản lý điểm dừng
                  </Button>
                </div>
                <div className="space-y-4 relative before:absolute before:left-7 before:top-4 before:bottom-4 before:w-0.5 before:bg-slate-100">
                  {stops.slice(0, 5).map((stop, idx) => (
                    <div key={stop.id} className="relative pl-14">
                      <div className="absolute left-4 top-1 w-6 h-6 rounded-full bg-os-primary text-white flex items-center justify-center text-[10px] font-bold border-4 border-white shadow-sm">
                        {stop.sequence_order}
                      </div>
                      <div className="flex items-center justify-between bg-slate-50 p-4 rounded-2xl border border-slate-100 hover:border-os-primary/30 transition-all cursor-pointer">
                        <div>
                          <p className="text-xs font-bold text-slate-800">{stop.zone?.zone_name}</p>
                          <p className="text-[10px] text-slate-400  font-bold">{stop.workload?.name || 'Kiểm tra tổng quát'}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-[10px] font-bold text-slate-700">{stop.tasks?.length || 0} công việc</p>
                          <p className="text-[10px] text-slate-400">{stop.estimated_duration_minutes || 0} phút</p>
                        </div>
                      </div>
                    </div>
                  ))}
                  {stops.length === 0 && (
                    <div className="pl-14 text-slate-400 text-xs italic py-4">Chưa có điểm dừng nào được thiết lập.</div>
                  )}
                </div>
              </Card>
            </>
          )}

          {activeTab === 'stops' && (
            <Card className="overflow-hidden border-none shadow-sm">
              <Table
                columns={[
                  { key: 'seq', title: 'TT', align: 'center' },
                  { key: 'zone', title: 'Điểm dừng (Zone)' },
                  { key: 'workload', title: 'Hạng mục' },
                  { key: 'tasks', title: 'CV', align: 'center' },
                  { key: 'time', title: 'TG (ph)', align: 'center' },
                ]}
                data={stops}
                isLoading={isLoadingStops}
                renderCell={(stop, key) => {
                  switch (key) {
                    case 'seq': return <span className="font-bold text-slate-400">{stop.sequence_order}</span>;
                    case 'zone': return <span className="text-sm font-bold text-slate-800">{stop.zone?.zone_name}</span>;
                    case 'workload': return <span className="text-xs text-slate-500 font-medium">{stop.workload?.name || '---'}</span>;
                    case 'tasks': return <span className="text-xs font-bold text-os-primary">{stop.tasks?.length || 0}</span>;
                    case 'time': return <span className="text-xs text-slate-500">{stop.estimated_duration_minutes || 0}</span>;
                    default: return null;
                  }
                }}
              />
            </Card>
          )}

          {(activeTab === 'tasks' || activeTab === 'history' || activeTab === 'map') && (
            <FeaturePlaceholder
              title={tabs.find(t => t.value === activeTab)?.label || 'Tính năng'}
              description="Phần này đang được tổng hợp dữ liệu từ quy trình thực địa."
            />
          )}
        </div>

        <div className="space-y-6">
          <Card className="p-6 bg-slate-900 text-white">
            <h4 className="font-bold mb-4 flex items-center gap-2">
              <i className="ri-direction-line text-os-primary" />
              Thông tin hành trình
            </h4>
            <div className="space-y-4">
              <div className="flex justify-between items-center text-xs">
                <span className="text-slate-400">Độ dài dự kiến</span>
                <span className="font-bold">4.2 km</span>
              </div>
              <div className="flex justify-between items-center text-xs">
                <span className="text-slate-400">Tần suất</span>
                <span className="font-bold">Hàng ngày (Daily)</span>
              </div>
              <div className="pt-4 border-t border-slate-800">
                <p className="text-[10px] text-slate-500 font-bold  mb-2">Trình tự tối ưu</p>
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-[11px] text-slate-300">
                    <div className="w-1.5 h-1.5 rounded-full bg-os-primary" />
                    Bắt đầu tại Zone A (Cổng chính)
                  </div>
                  <div className="flex items-center gap-2 text-[11px] text-slate-300">
                    <div className="w-1.5 h-1.5 rounded-full bg-slate-600" />
                    Kết thúc tại Zone D (Khu xử lý)
                  </div>
                </div>
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <h4 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
              <i className="ri-history-line text-slate-400" />
              Trạng thái thực hiện (Hôm nay)
            </h4>
            <div className="space-y-4">
              <div className="flex justify-between items-center text-xs">
                <span className="text-slate-500  font-bold">Tiến độ hiện tại</span>
                <span className="font-bold text-os-primary">0%</span>
              </div>
              <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                <div className="h-full bg-os-primary" style={{ width: '0%' }} />
              </div>
              <Alert variant="info" icon="ri-information-line">
                Tuyến này chưa được bắt đầu thực hiện trong ngày hôm nay.
              </Alert>
              <Button variant="primary" className="w-full" icon="ri-play-fill">Bắt đầu ngay</Button>
            </div>
          </Card>
        </div>
      </div>

      <RouteFormModal
        isOpen={showEdit}
        item={item}
        companyId={companyId}
        onClose={() => setShowEdit(false)}
      />
    </PageDetailLayout>
  );
}
